import { useCallback, useMemo, useState } from "react";

import { getUser } from "@/components/session/JwtTokenManager";
import { Button } from "@/components/ui/button";
import {
  CopyLinkButton,
  ShareAccess,
  ShareItemData,
  ShareList,
} from "@/components/ui/sharing";
import { SharePermissionState } from "@/types/permissions";
import { FullRoleplaySession } from "@/types/roleplays";

interface ManagePermissionsViewProps {
  sharedMembers: FullRoleplaySession["sharedMembers"];
  programs: FullRoleplaySession["programs"];
  owner: FullRoleplaySession["user"] | null;
  isAutoShared?: boolean;
  onRevokeAccess: (workspaceMemberId: string) => void;
  loading: boolean;
  onDone: () => void;
}

const PROGRAM_DISPLAY_LIMIT = 5;

export function ManagePermissionsView({
  sharedMembers,
  programs,
  owner,
  isAutoShared,
  onRevokeAccess,
  loading,
  onDone,
}: ManagePermissionsViewProps) {
  const currentUser = getUser();
  const [showAllPrograms, setShowAllPrograms] = useState(false);

  // Convert to ShareItemData format
  const shareItems = useMemo(() => {
    const items: ShareItemData[] = [];

    // Add owner
    if (owner) {
      items.push({
        type: "user" as const,
        item: {
          id: owner.id,
          email: owner.email || "",
          name: owner.name || undefined,
        },
        shareStatus: "VIEW" as SharePermissionState,
        isOwner: true,
        isMe: owner.id === currentUser?.id,
        canModifyPermissions: false,
      });
    }

    // Add shared members
    sharedMembers?.forEach((member) => {
      if (member.user) {
        items.push({
          type: "user" as const,
          item: {
            id: member.user.id,
            email: member.user.email || "",
            name: member.user.name || undefined,
          },
          shareStatus: "VIEW" as SharePermissionState,
          isMe: member.user.id === currentUser?.id,
          canModifyPermissions: true,
          availablePermissions: [],
        });
      }
    });

    // Add programs
    const programsToShow = showAllPrograms
      ? programs
      : programs?.slice(0, PROGRAM_DISPLAY_LIMIT);

    programsToShow?.forEach((program) => {
      items.push({
        type: "program" as const,
        item: {
          id: program.id,
          name: program.name || "",
          subtitle: "Program Leads",
        },
        shareStatus: "VIEW" as SharePermissionState,
        canModifyPermissions: false,
      });
    });

    return items;
  }, [owner, sharedMembers, programs, currentUser, showAllPrograms]);

  const handleUpdateShareStatus = useCallback(
    (index: number, status: SharePermissionState) => {
      if (status === "REMOVE") {
        // Find the corresponding shared member
        const ownerCount = owner ? 1 : 0;
        const memberIndex = index - ownerCount;

        if (memberIndex >= 0 && memberIndex < (sharedMembers?.length || 0)) {
          const member = sharedMembers?.[memberIndex];
          if (member?.id) {
            onRevokeAccess(member.id);
          }
        }
      }
    },
    [owner, sharedMembers, onRevokeAccess],
  );

  return (
    <>
      <ShareList
        items={shareItems}
        onUpdateShareStatus={handleUpdateShareStatus}
        loading={loading}
        title="People with access"
        showAllThreshold={PROGRAM_DISPLAY_LIMIT}
        onToggleShowAll={() => setShowAllPrograms(!showAllPrograms)}
        showingAll={showAllPrograms}
      />

      <ShareAccess
        visibilityScope="User"
        onChange={() => {}}
        canShareToWorkspace={false}
        infoMessage={
          isAutoShared ? (
            <div>
              <div className="font-medium text-sm mb-1">
                Session Shared With Admins
              </div>
              <div>
                This session is automatically shared with scenario owners,
                editors, and monitors for training purposes.
              </div>
            </div>
          ) : (
            "Workspace admins can see some information about this session in analytics."
          )
        }
        className="mt-4"
        title="General Access"
      />

      <div className="flex justify-between mt-8 items-center gap-1">
        <CopyLinkButton />
        <Button onClick={onDone} intent="primary" className="px-12">
          Done
        </Button>
      </div>
    </>
  );
}
